<template>
	<div class="resources-container">
		<div class="filters container-expanded container">
			<div class="text-search">
				<label for="search">Search</label>
				<input
					type="text"
					name="search"
					:value="rawSearch"
					placeholder="Search"
					@input="(event: Event) => handleSearchInputUpdate((event.target as HTMLInputElement).value)"
				/>
				<small v-if="showSearchHelper">Enter 3 or more characters to search</small>
			</div>

			<div class="tax-filters">
				<div
					v-if="categories"
					class="category-filter"
				>
					<label for="category">Categories</label>
					<div class="select-container select-container-inline">
						<select
							id="category"
							name="category"
							:value="categoryFilter"
							@input="(event: Event) => handleCategoryFilterUpdate((event.target as HTMLSelectElement).value)"
						>
							<option
								:key="''"
								:value="''"
							>
								All Categories
							</option>
							<option
								v-for="term in categories"
								:key="term.slug"
								:value="term.slug"
								v-html="term.name"
							/>
						</select>
					</div>
				</div>
				<div class="toggles-group">
					<div
						v-if="showActionPlanFilter"
						class="toggles toggles-action-plan"
					>
						<label
							for="action-plan-all"
							class="toggle"
							:class="{
								'toggle-active': !enableActionPlanFilter,
							}"
						>
							<input
								id="action-plan-all"
								v-model="enableActionPlanFilter"
								:value="false"
								type="radio"
								name="action-plan"
							/>
							All
						</label>
						<label
							for="action-plan-reco"
							class="toggle"
							:class="{
								'toggle-active': enableActionPlanFilter,
							}"
						>
							<input
								id="action-plan-reco"
								v-model="enableActionPlanFilter"
								:value="true"
								type="radio"
								name="action-plan"
							/>
							EF Reco
						</label>
					</div>

					<div
						v-for="[rFilterKey, rFilter] in getEntries(resourceFilters)"
						:key="rFilterKey"
						class="toggles"
						:class="`toggles-${rFilterKey}`"
					>
						<label
							v-for="filterValue in rFilter.options"
							:key="filterValue.key"
							:for="`${rFilterKey}-${filterValue.key}`"
							class="toggle"
							:class="{
								'toggle-active': filters[rFilterKey] === filterValue.key,
							}"
						>
							<input
								:id="`${rFilterKey}-${filterValue.key}`"
								type="radio"
								:name="filterValue.key"
								:checked="filters[rFilterKey] === filterValue.key"
								@input="handleFilterInputUpdate(rFilterKey, filterValue.key)"
								@click="handlePossibleUnclick(rFilterKey, filterValue.key)"
							/>
							{{ filterValue.singularName }}

							<ResourceAudienceComponent
								v-if="rFilterKey === resourceFilterKey.Audience"
								:size="13"
								:audience="genericKeyToAudienceKey(filterValue.key)"
							/>

							<ResourceTypeComponent
								v-if="rFilterKey === resourceFilterKey.Type"
								:small="true"
								:type="genericKeyToTypeKey(filterValue.key)"
								:inverted="filters[rFilterKey] === filterValue.key"
							/>
						</label>
					</div>

					<button
						class="btn btn-small btn-reset"
						@click="resetFilters"
					>
						Reset
					</button>
				</div>
			</div>
		</div>

		<div
			class="container-expanded"
			v-if="loadingWithNoResources"
		>
			<LoadingComponent :loading="true" />
		</div>
		<div
			v-else-if="filteredAndSearchedResources.length"
			class="resources"
		>
			<slot :resources="filteredAndSearchedResources">
				<ResourcesListItemComponent
					v-for="r in filteredAndSearchedResources"
					:key="r.ID"
					:resource="r"
				/>
			</slot>
		</div>
		<LoadingComponent
			v-else-if="searching"
			class="container-expanded"
			:loading="true"
		/>
		<div
			v-else
			class="no-results"
		>
			No resources found for your search.
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, type PropType, type SlotsType } from 'vue';
import { useResourceStore } from '@/stores/resourceStore';
import { mapActions, mapState, mapWritableState } from 'pinia';
import LoadingComponent from '@/components/shared/LoadingComponent.vue';
import ResourcesListItemComponent from '@/components/resources/ResourcesListItemComponent.vue';
import { getEntries } from '@/helpers';
import { type ResourceFilterKey, resourceFilters, resourceFilterKey, type ResourceAudienceKey, type ResourceFilterOptionKey, type ResourceTypeKey } from '@/interfaces/resourceFilterInterface';
import { type Resource } from '@/interfaces/resourceInterface';
import ResourceAudienceComponent from '@/components/resources/ResourceAudienceComponent.vue';
import ResourceTypeComponent from '@/components/resources/ResourceTypeComponent.vue';

export default defineComponent({
	components: { LoadingComponent, ResourcesListItemComponent, ResourceAudienceComponent, ResourceTypeComponent },
	props: {
		showActionPlanFilter: {
			type: Boolean,
			default: false,
		},
		presetFilters: {
			type: Object as PropType<Record<ResourceFilterKey | 'category', string>>,
			default() {
				return {};
			},
		},
	},
	data() {
		return {
			resourceFilterKey,
			resourceFilters,
			showSearchHelper: false,
		};
	},
	slots: Object as SlotsType<{
		default: {
			resources: Resource[];
		};
	}>,
	computed: {
		...mapWritableState(useResourceStore, ['enableActionPlanFilter']),
		...mapState(useResourceStore, ['categories', 'filteredAndSearchedResources', 'rawSearch', 'filters', 'searching', 'loading', 'loadingWithNoResources', 'categoryFilter']),
	},
	watch: {
		loading: {
			handler() {
				this.initFilters();
			},
			once: true,
		},
	},
	mounted() {
		this.initFilters();
	},
	methods: {
		getEntries,
		initFilters() {
			// This order so that params overwrite props
			this.checkPropsForFilters();
			this.checkParamsForFilters();
		},
		checkPropsForFilters() {
			getEntries(this.presetFilters).forEach(([filterName, filterValue]) => {
				if (filterName === 'category') {
					this.categoryFilter = filterValue;
					return;
				}

				if (filterName in resourceFilterKey) {
					this.filters[filterName] = filterValue;
					return;
				}
			});
		},
		checkParamsForFilters() {
			Object.values(resourceFilterKey).forEach((resouceFilterKey) => {
				const query = this.$route.query[resouceFilterKey] as string;

				if (!query) {
					return;
				}

				this.filters[resouceFilterKey] = query;
			});

			const catQuery = this.$route.query['category'] as string;
			if (catQuery) {
				this.categoryFilter = catQuery;
			}

			window.history.pushState({}, document.title, window.location.pathname);
		},
		handlePossibleUnclick(taxSlug: ResourceFilterKey, val: string) {
			if (this.filters[taxSlug] === val) {
				this.handleFilterInputUpdate(taxSlug, '');
			}
		},
		// ew because we can't cast in
		genericKeyToAudienceKey(key: ResourceFilterOptionKey) {
			return key as ResourceAudienceKey;
		},
		genericKeyToTypeKey(key: ResourceFilterOptionKey) {
			return key as ResourceTypeKey;
		},
		...mapActions(useResourceStore, ['resetFilters', 'handleSearchInputUpdate', 'handleFilterInputUpdate', 'handleCategoryFilterUpdate']),
	},
});
</script>

<style scoped lang="scss">
.page-title {
	display: flex;
	align-items: center;
	background-color: rgb(var(--rs-color-brand-cherry));
	height: 50px;

	h2 {
		margin: 0;
		color: rgb(var(--rs-color-brand-white));
		font-size: 20px;
		text-transform: initial;
	}
}

.resources-container {
	position: relative;
}

.filters {
	--filter-background: 136 156 180 / 50%;

	background-color: rgba(var(--filter-background));
	padding-top: var(--rs-layout-content-padding);
	padding-bottom: var(--rs-layout-content-padding);
}

.text-search {
	margin-bottom: var(--rs-layout-content-padding);

	label {
		display: none;
	}

	input[type='text'] {
		min-height: 44px;
		font-size: 20px;
	}
}

.tax-filters {
	.category-filter {
		margin-bottom: var(--rs-layout-content-padding);
		max-width: 400px;

		label {
			display: none;
		}

		.select-container {
			--rs-select-container-border-color: var(--filter-background);

			width: 100%;

			select {
				width: 100%;
				font-size: 1.4rem;
			}
		}
	}

	.toggles-group {
		display: inline-flex;
		flex-wrap: wrap;
		justify-content: space-between;
		gap: 10px;

		.toggles {
			display: flex;
			height: 30px;

			--rs-local-inner-padding: 7px;
			--rs-local-outer-padding: 9px;

			label {
				display: flex;
				position: relative;
				justify-content: center;
				align-items: center;
				cursor: pointer;
				border-right: 1px solid rgba(var(--filter-background));
				background-color: rgb(var(--rs-color-brand-white));
				padding-right: 26px;
				padding-left: var(--rs-local-inner-padding);
				overflow: hidden;
				font-size: 14px;
				line-height: 1;
				user-select: none;
				text-transform: capitalize;

				&:last-child {
					border-right: none;
					border-top-right-radius: var(--rs-border-radius);
					border-bottom-right-radius: var(--rs-border-radius);
					padding-right: 29px;
				}

				input[type='radio'] {
					display: none;
				}

				.resource-audience {
					top: 8px;
					right: 8px;
				}

				&.toggle-active {
					background-color: rgb(var(--rs-color-brand-navy) / 75%);
					color: white;
				}

				&:first-child {
					border-top-left-radius: var(--rs-border-radius);
					border-bottom-left-radius: var(--rs-border-radius);
					padding-left: var(--rs-local-outer-padding);
				}

				@media screen and (width >= 768px) {
					&:first-child {
						border-top-left-radius: var(--rs-border-radius);
						border-bottom-left-radius: var(--rs-border-radius);
						padding-left: var(--rs-local-outer-padding);
					}
				}
			}

			&.toggles-type {
				label {
					padding-right: 24px;

					.resource-type {
						top: 6px;
						right: 4px;
						bottom: auto;
					}

					&[for='type-video'] {
						padding-right: 30px;

						.resource-type {
							right: 7px;
						}
					}
				}
			}
		}

		button {
			--rs-color-btn-bg: var(--rs-color-brand-white);
			--rs-color-btn-font: var(--rs-color-brand-grey-text);
		}
	}
}

.resources {
	display: grid;
	position: relative;
	flex-direction: column;
	gap: 15px;
	margin: 20px 0;
	max-width: 1000px;
	font-size: 1rem;

	article {
		border-top: #dddddd 1px solid;
		padding-top: 16px;

		&:first-child {
			border-top: 0;
			padding-top: 0;
		}
	}
}

.no-results {
	padding: 20px 0;
}

@media screen and (width >= 1090px) {
	.tax-filters {
		display: flex;
		gap: 40px;

		> * {
			flex: 1;
		}

		.category-filter {
			margin-bottom: 0;
		}

		.toggles-group {
			justify-content: flex-end;
		}
	}
}
</style>
